import React from 'react';
import pick from 'lodash/pick';
import Measure from 'react-measure';
import { CSSTransitionGroup } from 'react-transition-group';
import { defineMessages, injectIntl } from 'react-intl';
import Carousel from 'brastrap/common/carousel/Carousel';
import Image from 'brastrap/containers/image/ImageContainer';
import VideoContainer from 'brastrap/editorial/video-container/VideoContainer';
import Prose from 'brastrap/helpers/s-prose/s-prose';
import SaveItemButton from 'brastrap/promotional/save-item-button/SaveItemButton';
import Grid from 'brastrap/helpers/l-grid/l-grid';
import ZoomableImage from './ProductGalleryZoom';
import { applyModifiers } from '../../utils';
import type { Feature as Props } from './product-gallery.types.js';
import { dataLayerAddToWishList } from '../../../../shared/utils/data-layer.js';

const messages = defineMessages({
  clickToZoom: {
    id: 'product-gallery.click-to-zoom',
    defaultMessage: 'Click to zoom',
  },
});

const STYLE_PREFIX = 'c-product-gallery';
const VIDEO_IMAGE = 'video';

const MagnifyingGlass = () => (
  <div className={`${STYLE_PREFIX}__magnify-icon`} />
);

const ProductGalleryMainCarousel = (props: Props) => {
  const {
    baseImageCarouselOptions,
    images,
    intl,
    getImageDimensions,
    onZoomedImageClick,
    isMobile,
    figureDimensions,
    isDesktop,
    isZoomed,
    stickers,
    video,
    baseStyle,
    receivedTouch,
    initialFocalPoint,
    isTouchDevice,
    aspectRatio,
    showZoomTag,
    setRefOnParent,
    onCarouselInitialised,
    handleTouchMove,
    handleFeaturedImageClick,
    product,
    selectedSkuCurrent,
    savedItemsMap,
    onSaveItemPress,
    arrows,
    onCurrentImageChange,
    onImageResize,
    currency,
  } = props;

  const carouselItems = images.map((image, key) => {
    const displayImage = image && image.type !== VIDEO_IMAGE;
    const displayVideo = image && !displayImage;
    const displayStickers = displayImage && !isZoomed;
    const displayMagnifyingGlass =
      displayImage && !isZoomed && (!isDesktop || receivedTouch);
    // Get the dimensions of the first image if playing the video
    // Use the dimensions to set the width and height of the video.
    const zoomableImage = displayVideo ? images[0] : image;

    // Figure dimensions store the width and height of the featured image.
    // Ascertained by react-measure.
    const containerWidth = figureDimensions && figureDimensions[0];

    // Hide the video through CSS
    // This is required to fix playback through iPads and to ensure a smoother transition
    // when clicking "off" the video by clicking another image in the gallery.
    const videoModifiers = ['product-gallery'];
    if (!displayVideo) videoModifiers.push('hidden');
    return (
      <figure className={`${STYLE_PREFIX}__feature`} key={image.id || key}>
        <div
          className={applyModifiers(
            `${STYLE_PREFIX}__base-image-container`,
            baseStyle
          )}
        >
          <Grid className="c-product-details__buttons-container c-product-gallery__save-item-button">
            <Grid.Unit>
              <SaveItemButton
                isForMainPdpImage
                className="c-product-save-item__button"
                isSizeSelected={!!selectedSkuCurrent}
                isItemSaved={
                  selectedSkuCurrent
                    ? savedItemsMap[
                        selectedSkuCurrent.skuCode || selectedSkuCurrent
                      ]
                    : savedItemsMap[product.styleColourCode]
                }
                onClick={() => {
                  const itemToSave = pick(
                    product,
                    'styleCode',
                    'styleColourCode'
                  );

                  dataLayerAddToWishList(currency, product);

                  if (selectedSkuCurrent) {
                    // One size items have a string for selected sku, instead of an object
                    itemToSave.skuCode =
                      selectedSkuCurrent.skuCode || selectedSkuCurrent;
                  }
                  return onSaveItemPress(itemToSave);
                }}
              />
            </Grid.Unit>
          </Grid>
          {displayImage && (
            <CSSTransitionGroup
              transitionName="click-to-zoom-tag"
              transitionEnterTimeout={0}
              transitionLeaveTimeout={600}
            >
              {showZoomTag ? (
                <div key="zoom-tag" className={`${STYLE_PREFIX}__zoom_note`}>
                  {intl.formatMessage(messages.clickToZoom).toUpperCase()}
                </div>
              ) : (
                <div key="placeholder-tag" />
              )}
              {displayMagnifyingGlass && <MagnifyingGlass />}
            </CSSTransitionGroup>
          )}
          <Measure
            onMeasure={({ width, height }) => {
              getImageDimensions([width, height]);
              onImageResize(height);
            }}
          >
            <div
              className={`${STYLE_PREFIX}__base-image`}
              onClick={handleFeaturedImageClick}
              onTouchMove={handleTouchMove}
            >
              <Image
                fetchPriority="high"
                alt={zoomableImage.alt}
                height={zoomableImage.height}
                // Force the image to render slightly wider
                // In the worker, the image is set to 550 width, but since PDP work, the main image can be wider than
                // 550 on the PDP. I have not changed it at the worker level as PLP also uses the same src (550 incl)
                // and we do not want to make images unnecessarily large for speed reasons. Amy has agreed 700 width
                // is good enough for PDP on most devices.
                // TODO: Revisit this if PDP carousel max-width is implemented
                src={`${zoomableImage.detail}&w=700`}
                width={zoomableImage.width}
              />
            </div>
          </Measure>
        </div>

        <ZoomableImage
          endZoom={onZoomedImageClick}
          displayMap={!isMobile}
          image={zoomableImage}
          figureDimensions={figureDimensions}
          initialFocalPoint={initialFocalPoint}
          isTouchDevice={isTouchDevice}
          stackFactor={isZoomed ? 1 : 0}
          zoomTransitionTime={300}
        />

        {displayStickers && stickers}

        {displayVideo && (
          <VideoContainer
            aspectRatio={aspectRatio}
            loader="activity"
            modifiers={videoModifiers}
            play={false}
            displayOverlay
            video={{ ...video, width: containerWidth }}
          />
        )}
        {image.description && (
          <div className="c-product-gallery__carousel-image-caption">
            <Prose>
              <figcaption>{image.description}</figcaption>
            </Prose>
          </div>
        )}
      </figure>
    );
  });

  return (
    <div className={`${STYLE_PREFIX}__carousel--feature`}>
      <Carousel
        {...{
          ...baseImageCarouselOptions,
        }}
        arrows={images.length > 1 && arrows}
        ref={carousel => setRefOnParent(carousel)}
        onInitialise={onCarouselInitialised}
        onCurrentImageChange={imageIndex => onCurrentImageChange(imageIndex)}
      >
        {carouselItems}
      </Carousel>
    </div>
  );
};

export default injectIntl(ProductGalleryMainCarousel);
